import React from 'react';
import styled from 'styled-components';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { MessageTracking } from '@99designs/messaging';
import { FullscreenOverlay } from '@99designs/spa-common';
import { FullscreenFrame } from '@99designs/spa-common';
import { FullscreenFrameMain } from '@99designs/spa-common';
import { FullscreenFrameSidebar } from '@99designs/spa-common';
import { FullscreenMain } from '@99designs/spa-common';
import { FullscreenMainHeader } from '@99designs/spa-common';
import { FullscreenMainBody } from '@99designs/spa-common';
import { AnnotationContext } from './AnnotationContext';
import FeedbackModeProvider from './FeedbackModeProvider/FeedbackModeProvider';
import FileOverlayContextProvider from './FileOverlayContext/FileOverlayContext';
import { FileOverlayTrackingProvider } from './FileOverlayTrackingContext';
import FilePane from './FilePane';
import Header from './Header/Header';
import PreviewAlert from './PreviewAlert';
import { ScribbleContext } from './ScribbleContext/ScribbleContext';
import { SelectedEventContext } from './SelectedEventContext';
import {
    AnnotationDisplayComponentType,
    CreateAnnotation,
    CreateAnnotationResult,
    File,
} from './types';
import usePresentationMode from './usePresentationMode/usePresentationMode';

export interface FileOverlayProps<A, S> {
    showFileOverlay: boolean;

    file: File;
    totalFilesCount: number;
    currentFileIndex: number;
    nextFile: () => void;
    previousFile: () => void;
    closeOverlayHandler: () => void;

    initialSelectedEventId?: string;
    selectedEventContext: SelectedEventContext;
    annotationContext: AnnotationContext<A>;
    createAnnotation: CreateAnnotation<A>;
    createAnnotationResult: CreateAnnotationResult;
    AnnotationDisplay: AnnotationDisplayComponentType;
    scribbleContext: ScribbleContext<S>;

    sidebarComponent: React.ReactNode;

    annotationSentTracking?: MessageTracking;
}

export const FullscreenFrameMainWrapper = styled.div`
    ${mediaQuery.between('small', 'medium')} {
        margin-bottom: ${spaces['1bu']};
    }
`;

function FileOverlay<A, S>({
    file,
    showFileOverlay,
    sidebarComponent,
    totalFilesCount,
    currentFileIndex,
    nextFile,
    previousFile,
    closeOverlayHandler,
    initialSelectedEventId,
    selectedEventContext,
    annotationContext,
    createAnnotation,
    createAnnotationResult,
    AnnotationDisplay,
    scribbleContext,
    annotationSentTracking,
}: FileOverlayProps<A, S>) {
    const { handleMaximize, handleMinimize, isPresentationMode } = usePresentationMode();

    const isSidebarHidden = !sidebarComponent || isPresentationMode;
    const navNext = currentFileIndex + 1 < totalFilesCount ? nextFile : undefined;
    const navPrev = currentFileIndex - 1 >= 0 ? previousFile : undefined;

    return (
        <FileOverlayTrackingProvider annotationSentTracking={annotationSentTracking}>
            <FileOverlayContextProvider file={file}>
                <FeedbackModeProvider>
                    <selectedEventContext.SelectedEventProvider
                        initialSelectedEventId={initialSelectedEventId}
                    >
                        <annotationContext.AnnotationProvider
                            AnnotationDisplay={AnnotationDisplay}
                            createAnnotationResult={createAnnotationResult}
                            createAnnotation={createAnnotation}
                            canAnnotate={file.viewerCanAnnotate}
                        >
                            <scribbleContext.ScribbleProvider
                                fileId={file.id}
                                canScribble={file.viewerCanScribble}
                                canViewScribble={file.viewerCanViewScribble}
                            >
                                {showFileOverlay && (
                                    <FullscreenOverlay isActive={true}>
                                        <FullscreenFrame>
                                            <FullscreenFrameMainWrapper>
                                                <FullscreenFrameMain
                                                    isSidebarHidden={isSidebarHidden}
                                                >
                                                    <FullscreenMain>
                                                        <FullscreenMainHeader>
                                                            <Header
                                                                totalNumberOfFiles={totalFilesCount}
                                                                currentPageNumber={
                                                                    currentFileIndex + 1
                                                                }
                                                                goBack={closeOverlayHandler}
                                                                navNext={navNext}
                                                                navPrev={navPrev}
                                                                isSidebarHidden={isSidebarHidden}
                                                                handleMaximize={handleMaximize}
                                                                handleMinimize={handleMinimize}
                                                                isPresentationMode={
                                                                    isPresentationMode
                                                                }
                                                                useSelectedEvent={
                                                                    selectedEventContext.useSelectedEvent
                                                                }
                                                                useAnnotation={
                                                                    annotationContext.useAnnotation
                                                                }
                                                                useScribble={
                                                                    scribbleContext.useScribble
                                                                }
                                                            />
                                                        </FullscreenMainHeader>
                                                        {file.isGeneratedPreview && (
                                                            <PreviewAlert
                                                                downloadUrl={file.downloadUrl}
                                                            />
                                                        )}
                                                        <FullscreenMainBody hasHeader>
                                                            <FilePane
                                                                file={file}
                                                                useSelectedEvent={
                                                                    selectedEventContext.useSelectedEvent
                                                                }
                                                                useAnnotation={
                                                                    annotationContext.useAnnotation
                                                                }
                                                                useScribble={
                                                                    scribbleContext.useScribble
                                                                }
                                                            />
                                                        </FullscreenMainBody>
                                                    </FullscreenMain>
                                                </FullscreenFrameMain>
                                            </FullscreenFrameMainWrapper>
                                            <FullscreenFrameSidebar
                                                isSidebarHidden={isSidebarHidden}
                                            >
                                                {sidebarComponent}
                                            </FullscreenFrameSidebar>
                                        </FullscreenFrame>
                                    </FullscreenOverlay>
                                )}
                            </scribbleContext.ScribbleProvider>
                        </annotationContext.AnnotationProvider>
                    </selectedEventContext.SelectedEventProvider>
                </FeedbackModeProvider>
            </FileOverlayContextProvider>
        </FileOverlayTrackingProvider>
    );
}

export default FileOverlay;
